/*
 *  -----------------------------------------------------------------------
 *    <copyright file="user-context.tsx" company="Triumph Research Intelligence">
 *        Copyright (C) 2019 by Triumph Research Intelligence. All rights reserved.
 *    </copyright>
 *   -----------------------------------------------------------------------
 *
 */

import React, { useState, useEffect, PropsWithChildren } from 'react';
import { useLogger } from 'hooks';
import Settings from 'settings/settings';
import { connectToServer } from 'utils/signalr-service';
import { getSessionStorageStudyId, setSessionStorageStudyId } from '@contexts/local-state-context';
import { IModule } from '@config/modules';
import { IRoute } from '@config/routes/interfaces';

export interface IOrganisation {
    audit: string;
    code: string;
    created: string;
    createdById: string;
    deleted: boolean;
    devices: string[];
    evaluationModels: any[];
    extraData: any;
    id: string;
    indications: any[];
    key: string;
    loginCode: string;
    members: any[];
    smMembers: any[];
    modules: string[];
    name?: string;
    opraTimestamp: string;
    primaryContact: any;
    products: any[];
    programs: any[];
    recordId: number;
    security: any;
    updated: string;
}

export interface IUser {
    defaultStudyId: string;
    email: string;
    firstName: string;
    id: string;
    isActive: boolean;
    isAdmin: boolean;
    isAuditAdmin: () => boolean;
    isReportAdmin: () => boolean;
    isObsAndLogsAdmin: () => boolean;
    isTrialOsAdmin?: (skipSystemLevel?: boolean) => boolean;
    lastName: string;
    organisation: IOrganisation;
    organisationId: string;
    organisationKey: string;
    phone: string;
    recordId: number;
    roleId: string;
    status: number;
    token: string;
    systemAdminLevel: number;
    // properties below are added inside useAuthorization hook
    isSystemAdmin: (level: number) => boolean; // level fromConstants.SystemAdminLevel.Reviewer
    isEnabled?: () => boolean;
    isActiveOrgMember?: () => boolean;
    isDisabled?: () => boolean;
    isOrgAdmin?: (skipSystemLevel?: boolean) => boolean;
    isStudyAdmin?: (skipSystemLevel?: boolean) => boolean;
    isSmStudyAdmin?: (skipSystemLevel?: boolean) => boolean;
    setUser?: () => void;
    can?: (what: any) => boolean;
    canRead?: (params: { permission: number; element: string; module: number }) => boolean;
    canWrite?: (params: { permission: number; element: string; module?: number }) => boolean;
    canWriteSystemAdminLevel?: (checkOrg?: boolean, checkStudy?: boolean, checkReport?: boolean) => boolean;
    canWriteSystemSmAdminLevel?: () => boolean;
    canReadStudyAdminLevel?: () => boolean;
    canReadSmStudyAdminLevel?: () => boolean;
    organisationHasModule?: (module: IModule) => boolean;
    hasModule?: (module: IModule) => boolean;
    admininstratorCanViewRoute?: (route: IRoute) => boolean;
    nonAdmininstratorCanViewRoute?: (route: IRoute) => boolean;
    forceResetPassword?: boolean;
}

export interface IUserContext {
    user: IUser | null;
    token?: string | null;
    setUser: Function;
    isLoggedIn: boolean;
    setIsLoggedIn: Function;
    setToken: Function;
    getToken: Function;
    clearToken: Function;
    studyId: string | null;
    setStudyId: Function;
    getStudyId: Function;
    setSiteId: Function;
    setSubjectId: Function;
    getSiteId: Function;
    clearStudyId: Function;
    passwordForResetPage: string;
    setPasswordForResetPage: Function;
    intercomToken?: string | null;
}

export const UserContext = React.createContext<IUserContext>({
    user: null,
    token: null,
    setUser: null,
    isLoggedIn: null,
    setIsLoggedIn: null,
    setToken: null,
    getToken: null,
    clearToken: null,
    studyId: null,
    setStudyId: null,
    getStudyId: null,
    setSiteId: null,
    getSiteId: null,
    clearStudyId: null,
    passwordForResetPage: null,
    setPasswordForResetPage: null,
    intercomToken: null,
    setSubjectId: null,
});

export const Permissions = {
    Read: 0x00,
    ReadWrite: 0x01,

    Disabled: 0x00,
    Enabled: 0x01,
};

export default function ({ children }: PropsWithChildren) {
    const Logger = useLogger();

    let sessionStorage: Storage;
    try {
        sessionStorage = window.sessionStorage;
    } catch (e) {
        Logger.warn(e);
    }

    const getTokenFromStorage = (): string | null => sessionStorage?.getItem('token');

    const clearStorageToken = () => {
        if (sessionStorage) sessionStorage.removeItem('token');
    };

    const clearStorageStudyId = () => {
        if (sessionStorage) sessionStorage.removeItem('studyId');
    };

    const [user, setUser] = useState(null);
    const [intercomToken, setIntercomToken] = useState(null);
    const [token, setToken] = useState(getTokenFromStorage());
    const [studyId, setStudyId] = useState(getSessionStorageStudyId());
    const [siteId, setSiteId] = useState(null);
    const [isLoggedIn, setIsLoggedIn] = useState(false);
    const [passwordForResetPage, setPasswordForResetPage] = useState(null);

    useEffect(() => {
        setSessionStorageStudyId(studyId);
    }, [studyId]);

    useEffect(() => {
        if (token) {
            Settings.token = token;

            if (sessionStorage) sessionStorage.setItem('token', token);

            // Fetch user from token
            fetch(`${Settings.api.url()}/api/me`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    Authorization: `Bearer ${token}`,
                },
            })
                .then((result) => result.json())
                .then((result) => {
                    const u = result.user;

                    if (!u || result.error) {
                        Logger.error(`Error getting user: ${token}: ${result.error || ''}`);
                        setUser(null);
                        setToken(null);
                        return;
                    }

                    // Associate the role with the user
                    u.role = result.role;

                    // And the organisation
                    u.organisation = result.organisation;

                    // And the AI apps the organisation is licensed for (ADO 91627)
                    u.licensedApps = result.licensedApps ?? [];

                    // And the login code
                    if (u.organisation) u.organisation.loginCode = result.loginCode;

                    setIntercomToken(result.intercomToken);
                    setUser(u);

                    // Connect to the SignalR server so we can pick up change/disconnect messages
                    connectToServer(u);
                })
                .catch((e) => {
                    Logger.error(`Exception getting user: ${token}: ${e}`);
                    setUser(null);
                    setToken(null);
                });
        } else {
            clearStorageToken();
        }
    }, [token]);

    const doSetToken = (t: string | null) => setToken(t);
    const doSetUser = (u: any) => setUser(u);
    const doSetStudyId = (s: string | null) => setStudyId(s);

    return (
        <UserContext.Provider
            value={{
                user,
                token,
                setUser,
                studyId,
                setStudyId,
                isLoggedIn,
                setIsLoggedIn,
                setSiteId,
                getSiteId: () => siteId,
                getStudyId: () => {
                    if (studyId) return studyId;

                    let stored = getSessionStorageStudyId();
                    if (stored === 'null') stored = null;
                    doSetStudyId(stored);

                    return stored;
                },
                clearStudyId: () => {
                    clearStorageStudyId();
                },
                setToken: doSetToken,
                getToken: () => {
                    if (token) return token;

                    const stored = getTokenFromStorage();
                    doSetToken(stored);

                    return stored;
                },
                clearToken: () => {
                    doSetUser(null);
                    doSetToken(null);
                    doSetStudyId(null);
                    clearStorageToken();
                    clearStorageStudyId();
                },
                setPasswordForResetPage,
                passwordForResetPage,
                intercomToken,
            }}
        >
            {children}
        </UserContext.Provider>
    );
}
